// src/components/email/EmailStats.jsx
import React, { useState, useEffect } from 'react';
import { 
  FiMail, FiSend, FiEye, FiMousePointer, FiTrendingUp, 
  FiTrendingDown, FiClock, FiBarChart2, FiPieChart, FiDownload
} from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';

const EmailStats = () => {
  const { userProfile } = useAuth();
  const organizationId = userProfile?.organizationId;
  const [stats, setStats] = useState({
    totalSent: 0,
    totalDelivered: 0,
    totalOpened: 0,
    totalClicked: 0,
    openRate: 0,
    clickRate: 0,
    bounceRate: 0,
    recentEmails: []
  });
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState('7d');

  useEffect(() => {
    if (organizationId) {
      fetchStats();
    }
  }, [organizationId, timeRange]);

  const fetchStats = async () => {
    try {
      setLoading(true);
      
      // In a real app, this would fetch from your backend
      // For now, we'll simulate with localStorage data
      const savedEmails = JSON.parse(localStorage.getItem(`emails-${organizationId}`) || '[]');
      
      const now = new Date();
      const timeRangeMs = {
        '7d': 7 * 24 * 60 * 60 * 1000,
        '30d': 30 * 24 * 60 * 60 * 1000,
        '90d': 90 * 24 * 60 * 60 * 1000
      };
      
      const cutoffDate = new Date(now.getTime() - timeRangeMs[timeRange]);
      const filteredEmails = savedEmails.filter(email => 
        new Date(email.sentAt) >= cutoffDate
      );
      
      const totalSent = filteredEmails.length;
      const totalDelivered = filteredEmails.filter(e => e.status === 'delivered' || e.status === 'opened').length;
      const totalOpened = filteredEmails.reduce((sum, e) => sum + (e.opens || 0), 0);
      const totalClicked = filteredEmails.reduce((sum, e) => sum + (e.clicks || 0), 0);
      
      const openRate = totalSent > 0 ? (totalOpened / totalSent * 100) : 0;
      const clickRate = totalOpened > 0 ? (totalClicked / totalOpened * 100) : 0;
      const bounceRate = totalSent > 0 ? (filteredEmails.filter(e => e.status === 'failed').length / totalSent * 100) : 0;
      
      setStats({
        totalSent,
        totalDelivered,
        totalOpened,
        totalClicked,
        openRate: Math.round(openRate * 10) / 10,
        clickRate: Math.round(clickRate * 10) / 10,
        bounceRate: Math.round(bounceRate * 10) / 10,
        recentEmails: filteredEmails.slice(0, 5)
      });
    } catch (err) {
      console.error('Error fetching stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportStats = () => {
    const data = {
      timeRange,
      exportedAt: new Date().toISOString(),
      stats
    };
    
    const dataStr = JSON.stringify(data, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
    
    const exportFileDefaultName = `email-stats-${timeRange}-${new Date().toISOString().split('T')[0]}.json`;
    
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const StatCard = ({ title, value, icon: Icon, change, isPercentage = false, color = 'blue' }) => {
    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      green: 'bg-green-50 text-green-600',
      purple: 'bg-purple-50 text-purple-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      red: 'bg-red-50 text-red-600'
    };

    return (
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900">
              {isPercentage ? `${value}%` : value.toLocaleString()}
            </p>
            {change !== undefined && (
              <div className={`flex items-center mt-2 text-sm ${change >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                {change >= 0 ? <FiTrendingUp size={16} /> : <FiTrendingDown size={16} />}
                <span className="ml-1">{Math.abs(change)}% from last period</span>
              </div>
            )}
          </div>
          <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
            <Icon size={24} />
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Email Analytics</h1>
        </div>
        
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded-lg"></div>
            ))}
          </div>
          <div className="h-64 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">Email Analytics</h1>
        <div className="flex items-center space-x-4">
          <select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="90d">Last 90 days</option>
          </select>
          
          <button
            onClick={exportStats}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
          >
            <FiDownload className="mr-2" size={16} />
            Export
          </button>
        </div>
      </div>

      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <StatCard
          title="Emails Sent"
          value={stats.totalSent}
          icon={FiSend}
          color="blue"
        />
        <StatCard
          title="Delivered"
          value={stats.totalDelivered}
          icon={FiMail}
          color="green"
        />
        <StatCard
          title="Opened"
          value={stats.totalOpened}
          icon={FiEye}
          color="purple"
        />
        <StatCard
          title="Clicked"
          value={stats.totalClicked}
          icon={FiMousePointer}
          color="yellow"
        />
      </div>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          title="Open Rate"
          value={stats.openRate}
          icon={FiBarChart2}
          isPercentage={true}
          color="green"
        />
        <StatCard
          title="Click Rate"
          value={stats.clickRate}
          icon={FiMousePointer}
          isPercentage={true}
          color="purple"
        />
        <StatCard
          title="Bounce Rate"
          value={stats.bounceRate}
          icon={FiTrendingDown}
          isPercentage={true}
          color="red"
        />
      </div>

      {/* Charts Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activity */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Recent Activity</h3>
            <FiClock className="text-gray-400" size={20} />
          </div>
          
          {stats.recentEmails.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FiMail size={32} className="mx-auto mb-2 text-gray-300" />
              <p>No recent email activity</p>
            </div>
          ) : (
            <div className="space-y-4">
              {stats.recentEmails.map((email, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <div className="text-sm font-medium text-gray-900 truncate">
                      {email.subject || 'No subject'}
                    </div>
                    <div className="text-xs text-gray-500">
                      To: {email.to?.map(r => r.email).join(', ') || 'No recipients'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className={`text-xs px-2 py-1 rounded-full ${
                      email.status === 'sent' ? 'bg-green-100 text-green-800' :
                      email.status === 'failed' ? 'bg-red-100 text-red-800' :
                      'bg-gray-100 text-gray-800'
                    }`}>
                      {email.status}
                    </div>
                    <div className="text-xs text-gray-500 mt-1">
                      {new Date(email.sentAt).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Performance Overview */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-medium text-gray-900">Performance Overview</h3>
            <FiPieChart className="text-gray-400" size={20} />
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Delivery Rate</span>
              <span className="text-sm font-medium">
                {stats.totalSent > 0 ? Math.round((stats.totalDelivered / stats.totalSent) * 100) : 0}%
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-green-600 h-2 rounded-full" 
                style={{ width: `${stats.totalSent > 0 ? (stats.totalDelivered / stats.totalSent) * 100 : 0}%` }}
              ></div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Open Rate</span>
              <span className="text-sm font-medium">{stats.openRate}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-purple-600 h-2 rounded-full" 
                style={{ width: `${stats.openRate}%` }}
              ></div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-gray-600">Click Rate</span>
              <span className="text-sm font-medium">{stats.clickRate}%</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div 
                className="bg-yellow-600 h-2 rounded-full" 
                style={{ width: `${stats.clickRate}%` }}
              ></div>
            </div>
          </div>
        </div>
      </div>

      {/* Insights */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Insights & Recommendations</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Performance Insights</h4>
            <div className="space-y-2">
              {stats.openRate < 20 && (
                <div className="flex items-start space-x-2 text-sm">
                  <div className="w-2 h-2 bg-yellow-400 rounded-full mt-2"></div>
                  <p className="text-gray-600">
                    Your open rate is below average. Consider improving your subject lines and sender reputation.
                  </p>
                </div>
              )}
              
              {stats.clickRate < 3 && stats.totalOpened > 0 && (
                <div className="flex items-start space-x-2 text-sm">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2"></div>
                  <p className="text-gray-600">
                    Low click-through rate. Try making your call-to-action more prominent and compelling.
                  </p>
                </div>
              )}
              
              {stats.bounceRate > 5 && (
                <div className="flex items-start space-x-2 text-sm">
                  <div className="w-2 h-2 bg-red-400 rounded-full mt-2"></div>
                  <p className="text-gray-600">
                    High bounce rate detected. Clean your email list and verify addresses before sending.
                  </p>
                </div>
              )}
              
              {stats.openRate >= 20 && stats.clickRate >= 3 && stats.bounceRate <= 5 && (
                <div className="flex items-start space-x-2 text-sm">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2"></div>
                  <p className="text-gray-600">
                    Great performance! Your email campaigns are performing well across all metrics.
                  </p>
                </div>
              )}
            </div>
          </div>
          
          <div className="space-y-3">
            <h4 className="font-medium text-gray-900">Best Practices</h4>
            <div className="space-y-2 text-sm text-gray-600">
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                <p>Send emails on Tuesday-Thursday for better engagement</p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                <p>Keep subject lines under 50 characters</p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                <p>Include a clear call-to-action in every email</p>
              </div>
              <div className="flex items-start space-x-2">
                <div className="w-2 h-2 bg-blue-400 rounded-full mt-2"></div>
                <p>Test different send times to find your optimal schedule</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailStats;